import {
  ASSIGNED_SEATING,
  ASSIGNED_SEATING_MODES,
  common,
} from "@shared/constants";
import {
  isTicketTypeSoldOut,
  isTicketTypeWaitlisted,
} from "@shared/modules/ticket-types/utils/ticket-type-utils";
import Konva from "konva";
import SeatingService from "../services/SeatingService";
import {
  MapPermissionsType,
  PublicBaseLocationPermissionsSerializer,
  PublicBaseLocationSerializer,
  PublicTTPermissionsPayloadType,
  TransformType,
} from "../types/seating-types";
import SeatingUtilsFactory from "./SeatingUtilsFactory";

type CreateLocationParamsType = {
  location: PublicBaseLocationSerializer;
  transform: TransformType;
  hideLocationNames: boolean;
};

export default class GeneralAdmissionFactory {
  public seatingService: SeatingService;

  constructor(seatingService: SeatingService) {
    this.seatingService = seatingService;
    this.create = this.create.bind(this);
  }

  static readonly LABEL = {
    FONT_SIZE: 42,
    MIN_PADDING: 6,
    MIN_FONT_SIZE: 14,
  };

  static createLocationId(id: number): string {
    return `location-${id}`;
  }

  public isItemAssigner(): boolean {
    return (
      this.seatingService.mode === ASSIGNED_SEATING_MODES.EDIT_SEAT_TICKETS.id
    );
  }

  public isGroupSales(): boolean {
    return this.seatingService.mode === ASSIGNED_SEATING_MODES.GROUP_SALES.id;
  }

  public isAttendanceMap(): boolean {
    return this.seatingService.mode === ASSIGNED_SEATING_MODES.ATTENDANCE.id;
  }

  /**
   * Calculate optimal font size for all GA locations to ensure consistency.
   * Finds the most restrictive constraint across all locations.
   */
  static calculateUniformFontSize(
    locations: PublicBaseLocationSerializer[]
  ): number {
    let uniformFontSize = GeneralAdmissionFactory.LABEL.FONT_SIZE;
    const padding = GeneralAdmissionFactory.LABEL.MIN_PADDING * 2;
    const minFontSize = GeneralAdmissionFactory.LABEL.MIN_FONT_SIZE;

    // Calculate the minimum font size needed across all locations
    locations.forEach((location) => {
      const bounds = SeatingUtilsFactory.getPolygonBounds(
        location.display.points
      );
      const testLabel = new Konva.Text({
        text: location.identifier.toUpperCase(),
        fontSize: GeneralAdmissionFactory.LABEL.FONT_SIZE,
      });

      let fontSize = GeneralAdmissionFactory.LABEL.FONT_SIZE;

      // Shrink until it fits this location's bounds
      while (fontSize > minFontSize) {
        testLabel.fontSize(fontSize);
        const fitsWidth = testLabel.textWidth + padding <= bounds.width;
        const fitsHeight = testLabel.textHeight + padding <= bounds.height;

        if (fitsWidth && fitsHeight) {
          break;
        }

        fontSize -= 1;
      }

      // Use the most restrictive (smallest) font size across all locations
      uniformFontSize = Math.min(uniformFontSize, fontSize);
    });

    return uniformFontSize;
  }

  /**
   * Creates General Admission display group.
   */
  create({
    location,
    transform,
    hideLocationNames,
    uniformFontSize,
  }: CreateLocationParamsType & { uniformFontSize?: number }): {
    groupActive?: Konva.Group;
    groupDisabled?: Konva.Group;
  } {
    const { x, y, rotation } = transform;
    const { capacity, display, id, identifier } = location;
    const locationPermissions = this.seatingService.locationMap[id];

    /**
     * Check it seat is in user basket.
     * If permission exists then seat cannot be in basket.
     */
    const inBasket = this.seatingService.getLocationInCartPermissions(
      location.id
    );

    const bounds = SeatingUtilsFactory.getPolygonBounds(display.points);

    const { colorFill, colorStroke } = this.getLocationColors(
      !!locationPermissions,
      !!inBasket
    );

    const lines = new Konva.Line({
      points: display.points.flatMap((point: number[]) => point),
      fill: colorFill,
      stroke: colorStroke,
      closed: true,
      perfectDrawEnabled: false,
      listening: true,
      name: ASSIGNED_SEATING.GENERAL_ADMISSION.ID,
      data: { capacity, id, identifier },
      isActive:
        !!(locationPermissions || inBasket || this.isItemAssigner()) &&
        !this.isAttendanceMap(), // TODO - support GA locations in attendance mode
    });

    const label = GeneralAdmissionFactory.createLabel(identifier, {
      x: bounds.cx,
      y: bounds.cy,
      height: bounds.height,
      width: bounds.width,
      fill: colorStroke,
      hidden: false,
      name: "label",
      fontSize: uniformFontSize,
    });

    const group = new Konva.Group({
      x,
      y,
      name: "display",
      id: GeneralAdmissionFactory.createLocationId(location.id),
    });

    group.add(lines);
    if (!hideLocationNames) {
      group.add(label);
    }
    SeatingUtilsFactory.rotateAroundCenter(group, rotation ?? 0);

    return locationPermissions || inBasket || this.isItemAssigner()
      ? { groupActive: group }
      : { groupDisabled: group };
  }

  /**
   * Reduce font size while text width is larger than the
   * bound box's width or height.
   * Note: bounding box width and height appears to be the
   * width of the shape on the x-axis after rotation
   * therefore text width and height may be larger than the
   * shape's width and height. To get the proper width for
   * comparison we need to rotate the shape back to a neutral
   * position and then get the proper font size according to
   * this width.
   */
  static createLabel(
    text: string,
    {
      x = 0,
      y = 0,
      height,
      width,
      fill = "#555555",
      hidden = false,
      name,
      fontSize: providedFontSize,
    }: Konva.TextConfig & { fontSize?: number }
  ): Konva.Text {
    const fontSize =
      providedFontSize ?? GeneralAdmissionFactory.LABEL.FONT_SIZE;
    const label = new Konva.Text({
      x,
      y,
      text: text.toUpperCase(),
      fontSize,
      fill,
      listening: false,
      name,
    });

    label.x(label.x() - label.textWidth / 2);
    label.y(label.y() - label.textHeight / 2);

    if (hidden) {
      label.hide();
    }

    return label;
  }

  /**
   * Handle click event on a seat node.
   */
  async handleClick(node: Konva.Node, layer: Konva.Layer): Promise<void> {
    // location data
    const $data = {
      ...node.attrs.data,
      node,
      section: node?.parent?.parent?.attrs.identifier, // section name for GA - group sales needs this to show section name
    };

    /**
     * Another location is currently being processed.
     * Do nothing.
     */
    if (this.seatingService.isProcessing()) {
      return;
    }

    /**
     * Location Permissions
     *
     * All locations that are clickable should have permissions.
     * Location permissions may not have been fetched if a user has
     * location items in their basket and it is the last of the remaining items.
     * There is no way to tell if we currently have the proper permissions initialized
     * from this point, so refetch all permission for that location and add to the permissions map.
     */
    let $permissions: MapPermissionsType[] = [];
    if (
      !this.isItemAssigner() &&
      !this.isGroupSales() &&
      !this.seatingService.locationMap[$data.id]
    ) {
      $permissions = await this.setLocationPermissions($data.id);
    } else {
      $permissions = this.seatingService.locationMap[$data.id] || [];
    }

    const cb = SeatingService.getSelectCallBack();
    // get Konva nodes needed for animations
    const display = node.findAncestor(".display");
    const location = node.findAncestor(".location");
    const label = (display as Konva.Container).findOne(".label");
    // clear location cache for animations and node changes.
    location.clearCache();

    try {
      const response = await cb({ $data, $permissions, $name: node.name() });

      // return early and do not redraw location if:
      // 1. the response is null (example: seat selection modal is cancelled)
      if (response === null) {
        return;
      }
    } catch (e) {
      location.cache({
        pixelRatio: location.getAttr("cachedRatio"),
      });

      // return early and do not redraw location
      return;
    }

    let selected = false;
    let colorStroke: string;
    let colorFill: string;
    let labelStroke: string;

    // locations without permissions can be selected in the item assigner
    if (this.isItemAssigner()) {
      selected = !node.getAttr("selected");

      // set fill and label colors
      if ($permissions.length > 0) {
        ({ colorStroke, colorFill } =
          ASSIGNED_SEATING.GENERAL_ADMISSION.AVAILABLE);
      } else {
        ({ colorStroke, colorFill } =
          ASSIGNED_SEATING.GENERAL_ADMISSION.UNASSIGNED);
      }
      labelStroke = colorStroke;

      // set outline color
      if (selected) {
        colorStroke = "red";
      }
    }
    // else a location needs permissions to be selected
    else {
      let selected = false;
      for (let i = 0; i < $permissions.length; i += 1) {
        /**
         * Check to see if requested permission is now in SeatingService.cartPermissions.
         * If so and count > 0 then we have item selections from that location
         */
        const clp = this.seatingService.getCartPermission(
          Number($permissions[i].seatingItemPermissionId)
        );

        if (clp && clp.location && clp.location.count > 0) {
          selected = true;
          break;
        }
      }
      ({ colorFill, colorStroke } = selected
        ? ASSIGNED_SEATING.GENERAL_ADMISSION.CHOSEN
        : ASSIGNED_SEATING.GENERAL_ADMISSION.AVAILABLE);
      labelStroke = colorStroke;
    }

    node.setAttr("selected", selected);
    node.setAttr("fill", colorFill);
    node.setAttr("stroke", colorStroke);

    if (label) {
      label.setAttr("fill", labelStroke);
    }

    layer.batchDraw();
    location.cache({
      pixelRatio: location.getAttr("cachedRatio"),
    });
  }

  async handleBestAvailableGAClick(node: Konva.Node): Promise<void> {
    const $data = {
      ...node.attrs.data,
      node,
      section: node?.parent?.parent?.attrs.identifier, // section name for GA - group sales needs this to show section name
    };

    if (this.seatingService.isProcessing()) {
      return;
    }

    let $permissions: MapPermissionsType[] = [];
    if (
      !this.isItemAssigner() &&
      !this.isGroupSales() &&
      !this.seatingService.locationMap[$data.id]
    ) {
      $permissions = await this.setLocationPermissions($data.id);
    } else {
      $permissions = this.seatingService.locationMap[$data.id] || [];
    }

    const cb = SeatingService.getSelectCallBack();
    this.seatingService.clearSeatingObjectArray();

    try {
      const response = await cb({ $data, $permissions, $name: node.name() });

      // return early and do not redraw location if:
      // 1. the response is null (example: seat selection modal is cancelled)
      if (response === null) {
        return;
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("error:", e);
    }
  }

  async setLocationSelected(
    display: Konva.Group,
    select: boolean,
    layer: Konva.Layer
  ): Promise<void> {
    const location = display.findAncestor(".location");
    const ga = display.findOne(".general_admission");
    const label = display.findOne(".label");

    /**
     * Location Permissions
     *
     * All locations that are clickable should have permissions.
     * Location permissions may not have been fetched if a user has
     * location items in their basket. There is no way to tell if we currently
     * have the proper permissions initialized from this point, so refetch
     * all permission for that location and add to the permissions map.
     */
    const $data = ga.getAttr("data");
    await this.setLocationPermissions($data.id);

    const { colorFill, colorStroke } = select
      ? ASSIGNED_SEATING.GENERAL_ADMISSION.CHOSEN
      : ASSIGNED_SEATING.GENERAL_ADMISSION.AVAILABLE;

    ga.setAttr("selected", select);
    ga.setAttr("fill", colorFill);
    ga.setAttr("stroke", colorStroke);

    if (label) {
      label.setAttr("fill", colorStroke);
    }

    // clear location cache for animations and node changes.
    location.clearCache();
    layer.batchDraw();
    location.cache({
      pixelRatio: location.getAttr("cachedRatio"),
    });
  }

  getLocationColors(
    hasPermissions: boolean,
    selected: boolean
  ): {
    name: string;
    colorFill: string;
    colorStroke: string;
    strokeWidth: number;
    helpText: string;
  } {
    // TODO: support locations with attendance mode
    if (this.isAttendanceMap()) {
      return ASSIGNED_SEATING.GENERAL_ADMISSION.UNAVAILABLE;
    }

    // is selected
    if (selected) {
      return ASSIGNED_SEATING.GENERAL_ADMISSION.CHOSEN;
    }

    // is available
    if (hasPermissions) {
      return ASSIGNED_SEATING.GENERAL_ADMISSION.AVAILABLE;
    }

    // unassigned locations on item assigner
    if (this.isItemAssigner()) {
      return ASSIGNED_SEATING.GENERAL_ADMISSION.UNASSIGNED;
    }

    // is unavailable
    return ASSIGNED_SEATING.GENERAL_ADMISSION.UNAVAILABLE;
  }

  async setLocationPermissions(
    locationId: number
  ): Promise<MapPermissionsType[]> {
    let promise: PublicBaseLocationPermissionsSerializer[] = [];

    if (
      this.seatingService.locationMap[locationId] &&
      this.seatingService.spaceId
    ) {
      const params: PublicTTPermissionsPayloadType = {
        location: locationId,
        event: undefined,
        membership_benefit: undefined,
        space: this.seatingService.spaceId,
        basket_hold: this.seatingService.basketHoldID,
      };

      if (
        this.seatingService.seatingObjectId &&
        this.seatingService.mapItemType === common.SeatingMapItemType.EVENT
      ) {
        params.event = this.seatingService.seatingObjectId;
      } else if (
        this.seatingService.seatingObjectId &&
        this.seatingService.mapItemType === common.SeatingMapItemType.MEMBERSHIP
      ) {
        params.membership_benefit = this.seatingService.seatingObjectId;
      }

      promise =
        await this.seatingService.locationPermissionsRepo.getPermissions({
          params,
        });
    }

    /**
     * if item types returned, map returned data to match new
     * query and sort by item type order
     */
    const permissions = promise
      .map(
        ({
          id,
          ticket_type,
          membership_level,
          capacity_limit,
          location,
          stats,
        }) => {
          const returnedItemId = ticket_type ?? membership_level;
          return {
            seatingItemTypeId: returnedItemId,
            seatingItemPermissionId: id,
            // usage remainins = capcity limit - total claimed + claimed by user
            usage_remaining: capacity_limit - Number(stats?.total_claimed),
            capacity_limit,
            location,
          };
        }
      )
      .filter(
        ({ seatingItemTypeId }) =>
          this.seatingService.itemTypeMap[seatingItemTypeId]
      )
      .sort(this.seatingService.sortItemTypeMap);
    // add permission to location map
    this.seatingService.locationMap[locationId] = permissions;
    return permissions;
  }

  isGASoldOut(permissions: MapPermissionsType[]): boolean {
    const itemArray =
      permissions && permissions.length
        ? permissions.map(
            (p) => this.seatingService.itemTypeMap[p.seatingItemTypeId]
          )
        : [];
    return (
      itemArray &&
      itemArray.length > 0 &&
      itemArray.every(
        (t) => isTicketTypeSoldOut(t) && !isTicketTypeWaitlisted(t)
      )
    );
  }

  updateLocationColor(
    locNode: Konva.Node,
    locPerms: Array<MapPermissionsType>
  ): void {
    const display = locNode.findAncestor(".display");
    const location = display.findAncestor(".location");
    const label = (display as Konva.Container).findOne(".label");
    location.clearCache();

    const isSoldOut = this.isGASoldOut(locPerms);
    const hasPermissions = locPerms.length > 0;

    // logic for item assigner map
    // show the color as unavailable if there are no permissions assigned
    // show the color as available if any permissions have been assigned to the location (even if sold out)
    if (this.isItemAssigner() && hasPermissions) {
      const { colorFill, colorStroke } =
        ASSIGNED_SEATING.GENERAL_ADMISSION.AVAILABLE;
      locNode.setAttr("fill", colorFill);
      locNode.setAttr("stroke", colorStroke);

      if (label) {
        label.setAttr("fill", colorStroke);
      }
    }
    // logic for public map
    // show the color as unavailable if no permissions are assigned OR the location is sold out
    else if (!this.isItemAssigner() && (!hasPermissions || isSoldOut)) {
      const { colorFill, colorStroke } =
        ASSIGNED_SEATING.GENERAL_ADMISSION.UNAVAILABLE;
      locNode.setAttr("fill", colorFill);
      locNode.setAttr("stroke", colorStroke);

      if (label) {
        label.setAttr("fill", colorStroke);
      }
    }
    // else use the permissions array to determine color
    else {
      const { colorFill, colorStroke } = this.getLocationColors(
        hasPermissions,
        false
      );
      locNode.setAttr("fill", colorFill);
      locNode.setAttr("stroke", colorStroke);

      if (label) {
        label.setAttr("fill", colorStroke);
      }
    }
  }
}
